import { motion, useInView } from "framer-motion";
import { useRef, useState } from "react";
import { Mail, Phone, MapPin, Send, Loader2, CheckCircle, Sparkles } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { toast } from "sonner";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const contactInfo = [
  {
    icon: MapPin,
    label: "Headquarters",
    value: "7-1-235/42P42 Suresh Arcade, Prashant Nagar Colony, BK Guda, Begumpet, Secunderabad, Hyderabad - 500016, Telangana",
  },
  {
    icon: Mail,
    label: "Email",
    value: "info@tech4earth.net",
    link: "mailto:info@tech4earth.net",
  },
  {
    icon: Phone,
    label: "Phone",
    value: "+91 9900089415",
    link: "tel:+919900089415",
  },
];

const ContactSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    organization: "",
    message: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [newsletterEmail, setNewsletterEmail] = useState("");
  const [isSubscribing, setIsSubscribing] = useState(false);
  const [isSubscribed, setIsSubscribed] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await axios.post(`${API}/contact`, formData);
      setIsSubmitted(true);
      toast.success("Message sent successfully! We'll get back to you soon.");
      setFormData({ name: "", email: "", organization: "", message: "" });
      setTimeout(() => setIsSubmitted(false), 3000);
    } catch (error) {
      toast.error("Failed to send message. Please try again.");
      console.error("Contact form error:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleNewsletterSubmit = async (e) => {
    e.preventDefault();
    if (!newsletterEmail) {
      toast.error("Please enter your email address");
      return;
    }
    setIsSubscribing(true);

    try {
      await axios.post(`${API}/newsletter`, { email: newsletterEmail });
      setIsSubscribed(true);
      toast.success("Successfully subscribed to our newsletter!");
      setNewsletterEmail("");
      setTimeout(() => setIsSubscribed(false), 3000);
    } catch (error) {
      if (error.response?.status === 400) {
        toast.error("This email is already subscribed");
      } else {
        toast.error("Failed to subscribe. Please try again.");
      }
      console.error("Newsletter subscription error:", error);
    } finally {
      setIsSubscribing(false);
    }
  };

  return (
    <section
      id="contact"
      ref={ref}
      data-testid="contact-section"
      className="py-20 md:py-32 bg-gradient-to-br from-emerald-900 via-emerald-800 to-emerald-900"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20">
          {/* Left Column - Info */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8 }}
          >
            <span className="text-sm font-semibold uppercase tracking-[0.2em] text-emerald-300 mb-4 block">
              Get in Touch
            </span>
            <h2 className="font-heading text-3xl sm:text-4xl font-semibold text-white mb-6 tracking-tight">
              Join the Mobility Revolution
            </h2>
            <p className="text-emerald-100/80 leading-relaxed mb-10">
              Interested in becoming a member or learning more about our
              initiatives? We'd love to hear from you. Reach out to discuss how
              T4EM can support your sustainable mobility goals.
            </p>

            {/* Contact Info */}
            <div className="space-y-6">
              {contactInfo.map((item, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  animate={isInView ? { opacity: 1, y: 0 } : {}}
                  transition={{ delay: 0.2 + index * 0.1, duration: 0.5 }}
                  className="flex items-center gap-4"
                >
                  <div className="p-3 bg-emerald-700/50 rounded-xl">
                    <item.icon className="w-5 h-5 text-emerald-300" />
                  </div>
                  <div>
                    <div className="text-sm text-emerald-300/70">{item.label}</div>
                    {item.link ? (
                      <a href={item.link} target="_blank" rel="noopener noreferrer" className="text-white font-medium hover:text-emerald-300 transition-colors">
                        {item.value}
                      </a>
                    ) : (
                      <div className="text-white font-medium">{item.value}</div>
                    )}
                  </div>
                </motion.div>
              ))}
            </div>

            {/* Newsletter Subscription */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ delay: 0.6, duration: 0.5 }}
              className="mt-12 p-6 bg-emerald-800/50 rounded-2xl border border-emerald-700/30"
            >
              <h3 className="font-heading text-lg font-semibold text-white mb-2">
                Stay Updated
              </h3>
              <p className="text-sm text-emerald-200/70 mb-4">
                Subscribe to our newsletter for the latest mobility insights and
                event updates.
              </p>
              <form className="flex gap-2" onSubmit={handleNewsletterSubmit}>
                <Input
                  type="email"
                  placeholder="Enter your email"
                  value={newsletterEmail}
                  onChange={(e) => setNewsletterEmail(e.target.value)}
                  className="bg-emerald-900/50 border-emerald-600/30 text-white placeholder:text-emerald-300/50 focus:border-emerald-400 flex-1"
                  data-testid="newsletter-email-input"
                  disabled={isSubscribing || isSubscribed}
                />
                <Button
                  type="submit"
                  className="bg-white text-emerald-800 hover:bg-emerald-50"
                  data-testid="newsletter-subscribe-button"
                  disabled={isSubscribing || isSubscribed}
                >
                  {isSubscribing ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : isSubscribed ? (
                    <>
                      <CheckCircle className="w-4 h-4 mr-1" />
                      Done
                    </>
                  ) : (
                    <>
                      <Sparkles className="w-4 h-4 mr-1" />
                      Subscribe
                    </>
                  )}
                </Button>
              </form>
            </motion.div>
          </motion.div>

          {/* Right Column - Contact Form */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8 }}
          >
            <div className="bg-white rounded-3xl p-8 md:p-10 shadow-2xl">
              <h3 className="font-heading text-2xl font-semibold text-stone-900 mb-2">
                Send us a Message
              </h3>
              <p className="text-stone-500 mb-8">
                Fill out the form below and our team will respond shortly.
              </p>

              <form onSubmit={handleSubmit} className="space-y-5">
                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-2">
                    Full Name
                  </label>
                  <Input
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    placeholder="Your name"
                    className="bg-stone-50 border-stone-200 focus:border-emerald-500 focus:ring-emerald-500"
                    required
                    data-testid="contact-name-input"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-2">
                    Email Address
                  </label>
                  <Input
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    placeholder="your@email.com"
                    className="bg-stone-50 border-stone-200 focus:border-emerald-500 focus:ring-emerald-500"
                    required
                    data-testid="contact-email-input"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-2">
                    Organization
                  </label>
                  <Input
                    name="organization"
                    value={formData.organization}
                    onChange={handleInputChange}
                    placeholder="Company or organization name"
                    className="bg-stone-50 border-stone-200 focus:border-emerald-500 focus:ring-emerald-500"
                    data-testid="contact-organization-input"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-2">
                    Message
                  </label>
                  <Textarea
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    placeholder="Tell us about your interest in T4EM..."
                    rows={4}
                    className="bg-stone-50 border-stone-200 focus:border-emerald-500 focus:ring-emerald-500 resize-none"
                    required
                    data-testid="contact-message-input"
                  />
                </div>

                <Button
                  type="submit"
                  disabled={isSubmitting || isSubmitted}
                  className="w-full bg-emerald-700 hover:bg-emerald-800 text-white rounded-full py-6 text-base font-medium btn-shine"
                  data-testid="contact-form-submit"
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                      Sending...
                    </>
                  ) : isSubmitted ? (
                    <>
                      <CheckCircle className="w-5 h-5 mr-2" />
                      Message Sent!
                    </>
                  ) : (
                    <>
                      <Send className="w-5 h-5 mr-2" />
                      Send Message
                    </>
                  )}
                </Button>
              </form>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
